/**
 * K3 „Wer bestellt — und wer zahlt" (P2-V3 § 2.4): jede Anfo der 12 Monate bis zum PL-X-Datenstand nach ihrer Adresse,
 * mit Bezahlquote gegen dieselben 12 Monate ein Jahr früher (Zwei-Anteile-Test, Holm über drei Typen + Alle) und der
 * Lesezeile „mit derselben Mischung …". Grafik je Bestellquartal für alle drei Typen (Umschalter Bezahlquote | Anteil).
 * Typen und Anteile ohne vorläufige (Festlegung 14.10.). F7: „Bisher bezahlt" wählbar ohne Erwarteten Wert, „—" mit
 * Grund, wenn die Typen unterschiedlich alt sind. Fakten aus `helpers/anfoFacts.ts`.
 * Persona-Umbau C5 (28.10.2026): erste Karte des Kapitels; die Lesezeile steht als Befund oben; Typ-Farben seit Audit D13
 * (30.10.) aus den Slots 1–3 der Palette, runder Höchstwert; letzte Achsenbeschriftung nicht mehr abgeschnitten.
 */
import { FC, useMemo, useState } from 'react';
import { Box, ToggleButton, ToggleButtonGroup, Tooltip, useMediaQuery, useTheme } from '@mui/material';
import { ResponsiveLine } from '@nivo/line';
import ChartCard from '../../../components/auswertungen/ChartCard';
import InfoTip from '../../../components/InfoTip';
import SortableTable, { Spalte, spaltenMeta } from '../../../components/auswertungen/SortableTable';
import SpaltenMenue from '../../../components/auswertungen/SpaltenMenue';
import { Breite, useSpaltenwahl } from '../../../components/auswertungen/spaltenwahl';
import { Legende, TipRow, achse, runderHoechstwert, useVizStyle } from '../../../components/auswertungen/vizStyle';
import { BefundZeile, BezahltBuendel, Hinweis, int, Laden, nachGruppenReihenfolge, spanne, useLaden, vorlaeufigSatz } from '../../../components/auswertungen/kennzahlTeile';
import { formatEur, formatPct, spaltenTip, TERMS } from '../../../helpers/kpiDefinitions';
import { formatPeriode, KundentypKey, KundentypZeileP2 } from '../../../helpers/auswertungenData';
import { fetchKundentypGeteilt } from '../../../helpers/umsatzData';
import { kundentypBefund } from './befund';
import { DatenstandText, Kartenkopf, letzterTag, P2_GRUPPEN, QuoteZelle, schwellenAus } from './teile';

type TypKey = Exclude<KundentypKey, 'serieller_nichtzahler'>;
const TYPEN: TypKey[] = ['erstbesteller', 'frueher_gezahlt', 'frueher_nie_gezahlt'];
/** Ein Name je Typ in Tabelle und Grafik (`sektionLabel`); `label`/`short` lesen die alten Seiten. */
const TYP: Record<TypKey, { name: string; tip: string }> = {
  erstbesteller: { name: TERMS.erstbesteller.sektionLabel, tip: TERMS.erstbesteller.tip },
  frueher_gezahlt: { name: TERMS.frueherGezahlt.sektionLabel, tip: TERMS.frueherGezahlt.tip },
  frueher_nie_gezahlt: { name: TERMS.frueherNieGezahlt.sektionLabel, tip: TERMS.frueherNieGezahlt.tip },
};

const STANDARDS: Record<Breite, readonly string[]> = {
  breit: ['typ', 'anfos', 'anteil', 'quote'],
  mittel: ['typ', 'anfos', 'anteil', 'quote'],
  schmal: ['typ', 'anteil', 'quote'],
};

type Zeile = KundentypZeileP2 & { alle?: boolean };

/** „23.10.25 – 22.10.26" aus Datetime-Grenzen. */
const kurz = (von: string, bisExkl: string) => {
  const t = (iso: string) => `${iso.slice(8, 10)}.${iso.slice(5, 7)}.${iso.slice(2, 4)}`;
  return `${t(von)} – ${t(letzterTag(bisExkl))}`;
};

const KundentypKarte: FC = () => {
  const { data, fehler } = useLaden(fetchKundentypGeteilt);
  const viz = useVizStyle();
  const theme = useTheme();
  const breit = useMediaQuery(theme.breakpoints.up('md'));
  const [ansicht, setAnsicht] = useState<'quote' | 'anteil'>('quote');
  const s = schwellenAus(data);
  // D13 (Audit 30.10.2026): Kategorie-Farben aus den Slots 1–3 der Referenzpalette — die einzigen drei, die ALLE Paare
  // bestehen (Linien kreuzen sich, also --pairs all): validate_palette.js 30.10.2026 hell CVD ΔE 9,2 / Normalsicht 24,0,
  // dunkel 9,4 / 20,9. Kein Status-Rot mehr (Slot 8 lag 4,8 neben „kritisch") und kein Grau für eine Datenreihe.
  // Orange = „nie gezahlt" passt zur Seite (Rechnungsstufen: verloren = Orange); „früher gezahlt" bleibt gestrichelt.
  const farbe: Record<TypKey, string> = viz.dark
    ? { erstbesteller: '#3987e5', frueher_gezahlt: '#199e70', frueher_nie_gezahlt: '#d95926' }
    : { erstbesteller: '#2a78d6', frueher_gezahlt: '#1baf7a', frueher_nie_gezahlt: '#eb6834' };

  const linien = useMemo(() => {
    // Leere Quartale am Ende (noch keine Zahl für irgendeinen Typ) fallen weg — sonst bleibt rechts eine leere Achsenstelle.
    const roh = data?.quarters ?? [];
    let ende = roh.length;
    const hatWert = (q: (typeof roh)[number]) =>
      TYPEN.some((t) => {
        const x = q.by_type?.[t];
        return (ansicht === 'quote' ? x?.pay_rate : x?.share) != null;
      });
    while (ende > 0 && !hatWert(roh[ende - 1])) ende -= 1;
    const qs = roh.slice(0, ende);
    return TYPEN.map((t) => ({
      id: t,
      data: qs.map((q) => {
        const x = q.by_type?.[t];
        const y = ansicht === 'quote' ? (x?.pay_rate ?? null) : (x?.share ?? null);
        return { x: q.quarter, y, n: ansicht === 'quote' ? x?.mature_paid ?? 0 : x?.anfos ?? 0, von: ansicht === 'quote' ? x?.mature_anfos ?? 0 : q.anfos, jung: q.is_young };
      }),
    }));
  }, [data, ansicht]);
  // D13: runder Höchstwert (50 % statt „auto" knapp über dem Maximum), Achse und Gitter aus denselben Ticks.
  const skala = useMemo(() => runderHoechstwert(Math.max(0, ...linien.flatMap((l) => l.data.map((d) => d.y ?? 0))), 5), [linien]);

  const sm = data?.summary;
  const alle: Zeile | null = sm
    ? {
        type: 'erstbesteller',
        alle: true,
        anfos: sm.anfos,
        share: 100,
        mature_anfos: sm.mature_anfos,
        mature_paid: sm.mature_paid,
        pay_rate: sm.pay_rate,
        paid_revenue: 0,
        // Die API liefert `paid_per_anfo` auch für „Alle" (der Stufe-0-Typ kennt das Feld nicht).
        paid_per_anfo: (sm as { paid_per_anfo?: number | null }).paid_per_anfo ?? null,
        pay_rate_davor: sm.pay_rate_davor,
        // Codex 25.10.: ohne die Zählwerte zeigte die Vergleichsspalte „0 von 0" (der Stufe-0-Typ kennt sie nicht).
        mature_paid_davor: (sm as { mature_paid_davor?: number }).mature_paid_davor ?? 0,
        mature_anfos_davor: (sm as { mature_anfos_davor?: number }).mature_anfos_davor ?? 0,
        test: sm.test,
        payers: sm.payers,
        avg_age_days: sm.avg_age_days,
      }
    : null;
  const typZeilen: Zeile[] = (data?.rows ?? []).filter((r): r is Zeile => r.type !== 'serieller_nichtzahler');
  const vergleichbar = data?.buendel_vergleichbar ?? false;
  const spanneAlter = data?.alter_spanne;
  const buendelGrund = spanneAlter
    ? `Typen unterschiedlich alt (Ø ${int(Math.round(spanneAlter[0]))} bis ${int(Math.round(spanneAlter[1]))} Tage); bisher bezahlt ist hier nicht vergleichbar.`
    : 'Nicht vergleichbar.';

  // Reife Spannen jetzt und ein Jahr früher (Plan 2026-10-30-vergleichsfenster-drift) für den Tooltip der Quote.
  const reifText =
    data?.window_reif && data.window_davor_reif
      ? `Anfos ${spanne(data.window_reif.erster_tag, data.window_reif.letzter_tag)}, gegen dieselbe Spanne ein Jahr früher (${spanne(data.window_davor_reif.erster_tag, data.window_davor_reif.letzter_tag)}).`
      : 'Gegen dieselbe Spanne ein Jahr früher.';
  const baue = (sichtbar: ReadonlySet<string>): Spalte<Zeile>[] => [
    {
      key: 'typ',
      label: 'Kundentyp',
      gruppe: 'Kundentyp',
      pflicht: true,
      minWidth: 170,
      render: (r) =>
        r.alle ? (
          <Box sx={{ lineHeight: 1.3 }}>
            Alle Anfos
            {!sichtbar.has('anfos') && (
              <Box component="span" sx={{ display: 'block', fontSize: 11, color: 'text.secondary', fontWeight: 400 }}>
                {int(r.anfos)} Anfos
              </Box>
            )}
          </Box>
        ) : (
          <Box sx={{ lineHeight: 1.3 }}>
            <Box component="span" sx={{ fontWeight: 600, display: 'inline-flex', alignItems: 'center' }}>
              {TYP[r.type as TypKey].name}
              <InfoTip text={TYP[r.type as TypKey].tip} />
            </Box>
            {!sichtbar.has('anfos') && (
              <Box component="span" sx={{ display: 'block', fontSize: 11, color: 'text.secondary' }}>
                {int(r.anfos)} Anfos
              </Box>
            )}
            {r.type === 'frueher_nie_gezahlt' && (r.serial ?? 0) > 0 && (
              <Box component="span" sx={{ display: 'flex', alignItems: 'center', fontSize: 11, color: 'text.secondary' }}>
                {TERMS.seriellerNichtzahler.unterzeile.replace('{n}', int(r.serial))}
                <InfoTip text={TERMS.seriellerNichtzahler.tip} />
              </Box>
            )}
          </Box>
        ),
    },
    {
      key: 'anfos',
      label: TERMS.anfos.label,
      tip: spaltenTip('anfos', `Ohne vorläufig ergänzte: sie haben noch keine Adresse und stehen nur in der Summe.`),
      gruppe: 'Menge',
      align: 'right',
      render: (r) =>
        r.alle && sm && sm.provisional > 0 ? (
          <Box sx={{ lineHeight: 1.25 }}>
            {int(r.anfos)}
            <Box component="span" sx={{ display: 'block', fontSize: 11, color: 'text.secondary', fontWeight: 400 }}>
              dazu {int(sm.provisional)} vorläufig, ohne Kundentyp
            </Box>
          </Box>
        ) : (
          int(r.anfos)
        ),
    },
    {
      key: 'anteil',
      label: TERMS.anteilAnAllenAnfos.label,
      tip: `${TERMS.anteilAnAllenAnfos.tip} Ohne vorläufig ergänzte Anfos.`,
      gruppe: 'Kundentyp',
      pflicht: true,
      align: 'right',
      render: (r) => formatPct(r.share),
    },
    {
      key: 'quote',
      label: TERMS.bezahlquote.label,
      tip: TERMS.bezahlquote.tip,
      einheit: `Anfos ab ${s.reife_tage} Tagen`,
      gruppe: 'Zahlung',
      pflicht: true,
      align: 'right',
      render: (r) => <QuoteZelle rate={r.pay_rate} bezahlt={r.mature_paid} reif={r.mature_anfos} jung={r.is_young} test={r.test} tipZusatz={reifText} />,
    },
    {
      key: 'davor',
      label: TERMS.bezahlquoteDavor.label,
      tip: TERMS.bezahlquoteDavor.tip,
      gruppe: 'Trend',
      align: 'right',
      render: (r) => <QuoteZelle rate={r.pay_rate_davor ?? null} bezahlt={r.mature_paid_davor ?? 0} reif={r.mature_anfos_davor ?? 0} />,
    },
    {
      key: 'buendel',
      label: TERMS.bezahltJeAnfo.label,
      tip: `${TERMS.bezahltJeAnfo.tip} Hier ausnahmsweise ohne Erwarteten Wert daneben: Der würde das Angebot bewerten, nicht den Kunden.`,
      einheit: 'bezahlte Anfos · Ø Alter',
      gruppe: 'Zahlung',
      align: 'right',
      render: (r) => {
        if (!vergleichbar)
          return (
            <Tooltip title={buendelGrund} arrow placement="top">
              <Box component="span" sx={{ cursor: 'help' }}>
                —
              </Box>
            </Tooltip>
          );
        return <BezahltBuendel wert={formatEur(r.paid_per_anfo, 0)} zahler={r.payers ?? 0} alterTage={r.avg_age_days == null ? null : Math.round(r.avg_age_days)} />;
      },
    },
  ];
  const meta = nachGruppenReihenfolge(spaltenMeta(baue(new Set())), P2_GRUPPEN);
  const wahl = useSpaltenwahl('umsatzKundentyp', meta, STANDARDS);
  const sichtbar = new Set(wahl.sichtbar);
  // Schmal (< 640 px) ohne Mindestbreite der Namensspalte: lief bei 267 px Karte 43 px über (Abnahme 25.10.2026).
  const spalten = baue(sichtbar)
    .filter((c) => sichtbar.has(c.key))
    .map((c) => (c.key === 'typ' && wahl.breite === 'schmal' ? { ...c, minWidth: undefined } : c));

  // Befund (C5, früher Lesezeile): Alle davor → jetzt, je Typ, und die Mischung.
  const befund =
    data && sm
      ? kundentypBefund({
          alle: sm,
          typen: typZeilen.map((r) => ({ name: TYP[r.type as TypKey].name, pay_rate: r.pay_rate, pay_rate_davor: r.pay_rate_davor, test: r.test })),
          mischung_quote: data.mischung_quote,
          fensterDavor: data.window_davor_reif
            ? spanne(data.window_davor_reif.erster_tag, data.window_davor_reif.letzter_tag)
            : data.window_davor
              ? spanne(data.window_davor.von.slice(0, 10), letzterTag(data.window_davor.bis))
              : null,
        })
      : null;

  return (
    <ChartCard
      title="Wer bestellt — und wer zahlt"
      anker="wer-bestellt-wer-zahlt"
      actions={<SpaltenMenue spalten={meta} wahl={wahl} />}
      howToRead="Jede Anfo nach ihrer Adresse: bestellt zum ersten Mal, hat schon einmal gezahlt, oder hat früher bestellt und nie gezahlt. Die Grafik zeigt je Quartal, wie jede Gruppe zahlt (Umschalter: wie groß sie ist). Vorläufig ergänzte Anfos haben noch keine Adresse und stehen nur in der Summe."
      terms={[
        { label: TERMS.erstbesteller.sektionLabel, tip: TERMS.erstbesteller.tip },
        { label: TERMS.frueherGezahlt.sektionLabel, tip: TERMS.frueherGezahlt.tip },
        { label: TERMS.frueherNieGezahlt.sektionLabel, tip: TERMS.frueherNieGezahlt.tip },
        { label: TERMS.seriellerNichtzahler.label, tip: TERMS.seriellerNichtzahler.tip },
        { label: TERMS.abweichungZeitraumMarke.label, tip: TERMS.abweichungZeitraumMarke.tip },
        { label: TERMS.doppelbestellung.label, tip: TERMS.doppelbestellung.tip },
        ...(sm && sm.provisional > 0 ? [{ label: TERMS.vorlaeufig.label, tip: TERMS.vorlaeufig.tip }] : []),
      ]}
      source="PL-X-Bestellungen und Zahlungen (alle Bestellungen derselben Adresse)"
      freshnessKey="plx_orders"
    >
      <Box ref={wahl.messRef}>
        {!data || !sm || !alle ? (
          <Laden fehler={fehler} />
        ) : (
          <>
            {befund && <BefundZeile text={'text' in befund ? befund.text : undefined} fehlt={'fehlt' in befund ? befund.fehlt : null} />}
            <Kartenkopf>
              Tabelle: Bestellungen {kurz(data.window.von, data.window.bis)}, bis <DatenstandText ds={data.datenstand} /> · 12 Monate
              {data.quarters.length > 0 &&
                ` · Grafik: ${formatPeriode(data.quarters[0].quarter)} – ${formatPeriode(data.quarters[data.quarters.length - 1].quarter)}`}
            </Kartenkopf>
            <SortableTable
              rows={typZeilen}
              // Summenzeile „Alle Anfos" mit denselben Zellen wie die Typen (Werte aus dem Backend).
              columns={spalten.map((c) => ({ ...c, summary: c.render(alle, { sortKey: null, sortGrund: null, ebene: 0 }) }))}
              rowKey={(r) => r.type}
              maxHeight={null}
              eng={wahl.breite === 'schmal'}
            />
            <Box sx={{ mt: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 1 }}>
                <ToggleButtonGroup size="small" exclusive value={ansicht} onChange={(_, v) => v && setAnsicht(v)} aria-label="Grafik zeigt">
                  <ToggleButton value="quote" sx={{ textTransform: 'none', py: 0.25 }}>
                    {TERMS.bezahlquote.label} je Typ
                  </ToggleButton>
                  <ToggleButton value="anteil" sx={{ textTransform: 'none', py: 0.25 }}>
                    Anteil je Typ
                  </ToggleButton>
                </ToggleButtonGroup>
                <Legende items={TYPEN.map((t) => ({ label: TYP[t].name, color: farbe[t], shape: 'line' as const, gestrichelt: t === 'frueher_gezahlt' }))} />
              </Box>
              <Box sx={{ height: 240 }}>
                <ResponsiveLine
                  data={linien}
                  colors={(l) => farbe[String(l.id) as TypKey]}
                  // Rechts Platz für die halbe letzte Achsenbeschriftung („4. Q. 26" lief bei 16 px über den Rand, C5).
                  margin={{ top: 12, right: 32, bottom: 32, left: 48 }}
                  xScale={{ type: 'point' }}
                  yScale={{ type: 'linear', min: 0, max: skala.max }}
                  theme={viz.nivo}
                  animate={false}
                  lineWidth={2}
                  // Eigene Linienebene: „früher gezahlt" gestrichelt — zweites Merkmal neben der Farbe.
                  layers={[
                    'grid',
                    'markers',
                    'axes',
                    'areas',
                    'crosshair',
                    ({ series, lineGenerator }) => (
                      <g>
                        {series.map((serie) => (
                          <path
                            key={String(serie.id)}
                            d={lineGenerator(serie.data.map((d) => d.position)) ?? undefined}
                            fill="none"
                            stroke={serie.color}
                            strokeWidth={2}
                            strokeDasharray={serie.id === 'frueher_gezahlt' ? '6 4' : undefined}
                          />
                        ))}
                      </g>
                    ),
                    'points',
                    'slices',
                    'mesh',
                    'legends',
                  ]}
                  pointSize={8}
                  pointColor={{ from: 'color' }}
                  pointBorderWidth={2}
                  pointBorderColor={{ theme: 'background' }}
                  // Junge Quartale (mehr als 20 % der Anfos unter der Reife) hohl: ihre Quote steigt noch.
                  pointSymbol={({ size, color, datum }) => {
                    const jung = Boolean((datum as unknown as { jung?: boolean }).jung);
                    return <circle r={size / 2} fill={jung ? theme.palette.background.default : color} stroke={jung ? color : theme.palette.background.default} strokeWidth={2} />;
                  }}
                  enableGridX={false}
                  gridYValues={skala.ticks}
                  axisLeft={{ tickSize: 0, tickPadding: 6, tickValues: skala.ticks, format: achse('%') }}
                  axisBottom={{
                    tickSize: 0,
                    tickPadding: 6,
                    tickValues: (linien[0]?.data ?? []).map((d) => d.x).filter((_, i, a) => breit || (a.length - 1 - i) % 2 === 0),
                    format: (v) => formatPeriode(String(v)),
                  }}
                  enableSlices="x"
                  sliceTooltip={({ slice }) => {
                    const p0 = slice.points[0]?.data as unknown as { x: string; jung: boolean };
                    return (
                      <Box sx={{ ...(viz.nivo.tooltip as { container: object }).container, px: 1.5, py: 1 }}>
                        <Box sx={{ fontWeight: 600, mb: 0.5 }}>
                          {formatPeriode(String(p0?.x))}
                          {p0?.jung ? ' · jung' : ''}
                        </Box>
                        {slice.points.map((p) => {
                          const d = p.data as unknown as { y: number | null; n: number; von: number };
                          const id = String(p.id).split('.')[0] as TypKey;
                          return <TipRow key={p.id} color={farbe[id]} label={TYP[id].name} value={`${formatPct(d.y)} (${int(d.n)} von ${int(d.von)})`} />;
                        })}
                      </Box>
                    );
                  }}
                />
              </Box>
            </Box>
            <Hinweis>
              „{TERMS.frueherGezahlt.sektionLabel}“ prüft den heutigen Zahlstand der früheren Bestellungen, nicht den damaligen. Hohle Punkte: junge
              Quartale, dort steigt die Quote noch. Doppelbestellungen in den 12 Monaten: {int(sm.duplicates)} — PL-X storniert die meisten selbst.{' '}
              {vorlaeufigSatz(sm.provisional)}
            </Hinweis>
          </>
        )}
      </Box>
    </ChartCard>
  );
};

export default KundentypKarte;
